import React, { CSSProperties, useEffect, useRef } from 'react';
import { useAppSelector } from 'shared/store';
import { unsubscribeToWidgetRrweb, subscribeToWidgetRrweb } from 'shared/realtime';
import { makeClearingEvent } from 'shared/utils/rrweb-clearing-event';
import { SbReplayer } from 'shared/utils/sb-rrweb';
import WidgetVideoInjector from '../WidgetVideoInjector';
import log from 'shared/utils/log';
import { SbRrwebEvents, eventWithTime, ANY_RID } from 'sb-lib/types';
import './style.less';
import clsx from 'clsx';

const rrwebIframeStyles = [
  `iframe { display: none; }`,
  // Disallow any interaction with the widget element.
  '.WIDGETROOT > * { cursor: not-allowed !important; }',
  '.WIDGETROOT > * > * { pointer-events: none !important; }',
  // Keep in sync with LIVE_PLAYBACK_HIDE_CLASS in widget/src/app/utils/constants.ts.
  '.servicebell--live-playback-hide { display: none !important; }',
  // Hide video controls
  `#sbstyle video::-webkit-media-controls { display: none; }`,
  // Hide spy opt-in modal for widget rrweb
  `#__sb_widget_spy_optin_modal_id__ { display: none !important; }`,
];

type Props = { showMouse: boolean; style: CSSProperties; innerStyle: CSSProperties };
export default function WidgetRrweb(props: Props) {
  const { replayerMode } = useAppSelector(s => s.dashboard);
  const { showMouse, style, innerStyle } = props;
  const rrwebIframeWrapper = useRef<null | HTMLDivElement>(null);
  const rrweb = useRef<null | SbReplayer>(null);
  const innerStyleRef = useRef<CSSProperties>(innerStyle);

  // react to replayer mode changes
  useEffect(() => {
    if (replayerMode === 'single' && rrweb.current) {
      log.debug(`Replayer mode is "single", will send clearing event to widget replayer`);
      // clear widget replayer content
      rrweb.current.addEvent(makeClearingEvent(Date.now()), ANY_RID);
    }
  }, [replayerMode]);

  // feed widget rrweb events into replayer
  useEffect(() => {
    rrweb.current = makeReplayer();

    const handler: (event: SbRrwebEvents) => void = event => {
      if (rrweb.current) {
        const addEvent = rrweb.current.addEvent.bind(rrweb.current);
        event.events.forEach(x => addEvent(x as eventWithTime, event.rid, event.mode));
      }
    };

    subscribeToWidgetRrweb(handler);

    return () => {
      if (rrweb.current) {
        // quell pending events
        rrweb.current.clearReplayer();
      }
      unsubscribeToWidgetRrweb(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeReplayer = () => {
    if (!rrwebIframeWrapper.current) {
      throw new Error('Cannot make rrweb replayer without wrapping element');
    }
    const replayer = new SbReplayer('Widget', [], {
      root: rrwebIframeWrapper.current,
      liveMode: true,
      pauseAnimation: false,
      triggerFocus: false,
      insertStyleRules: rrwebIframeStyles,
      mouseTail: false,
    });
    replayer.iframe.setAttribute('allow', 'autoplay');
    replayer.iframe.setAttribute('tabindex', '-1');
    replayer.onSnapshotBuilt(() => applyInnerStyle());
    return replayer;
  };

  // set style on rrweb controlled .replayer-wrapper el
  const applyInnerStyle = () => {
    const style = innerStyleRef.current;
    if (style && rrwebIframeWrapper.current) {
      const $repWrap = rrwebIframeWrapper.current.querySelector(
        '.replayer-wrapper',
      ) as HTMLElement;
      if ($repWrap) {
        Object.keys(style).forEach(k => {
          ($repWrap.style as any)[k] = style[k as keyof CSSProperties];
        });
      }
    }
  };

  useEffect(() => {
    innerStyleRef.current = innerStyle;
    applyInnerStyle();
  }, [innerStyle]);

  return (
    <>
      <div
        className={clsx('WidgetRrweb', !showMouse && 'hide-mouse')}
        style={style}
        ref={rrwebIframeWrapper}
      />
      <WidgetVideoInjector
        rrwebIframeWrapper={rrwebIframeWrapper}
        isWidgetStream={true}
      />
    </>
  );
}
